import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { FinishWebAuthnAuthenticationResponse } from '@cash-web/protos/squareup/cash/janus/api/WebAuthn.pb'
import { useMutationFinishWebAuthnAuthentication } from '@cash-web/shared-data-access-api/react-query/useMutationFinishWebAuthnAuthentication'
import { useMutationStartWebAuthnAuthentication } from '@cash-web/shared-data-access-api/react-query/useMutationStartWebAuthnAuthentication'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { createSingleUseFlowToken } from '@cash-web/shared-util-fetch'
import { WebResponseError, WebResponseSuccess } from '@squareup/customer-data-events-web'

import { getHeadersForWebAuthn } from '../getHeadersForWebAuthn'
import { createCredential } from './createCredential'
import { decodeOptions } from './decodeOptions'
import { encodeCredential } from './encodeCredential'

export function useAuthenticatePasskey({
  onSuccess,
  onError,
  onShowPasskeyDialog,
}: {
  onSuccess?: (response: FinishWebAuthnAuthenticationResponse, flowToken: string) => void
  onError: () => void
  onShowPasskeyDialog?: () => void
}) {
  const { t } = useTranslation()
  const logCdfEvent = useLogCashWebCdfEvent()
  const [flowToken] = useState(createSingleUseFlowToken())

  const defaultErrorMsg = t('An error occurred while authenticating your passkey.', {
    context: 'error',
  })

  const finishWebAuthnAuthenticationApiNameForLogging = 'finish-webauthn-authentication'
  const {
    mutate: finishWebAuthnAuthentication,
    isLoading: isFinishLoading,
    error: finishError,
  } = useMutationFinishWebAuthnAuthentication({
    options: { optionalRequestHeaders: getHeadersForWebAuthn(), flowToken },
    mutationOptions: {
      onSuccess: async response => {
        logCdfEvent(WebResponseSuccess({ apiName: finishWebAuthnAuthenticationApiNameForLogging }))
        onSuccess?.(response, flowToken)
      },
      onError: e => {
        logCdfEvent(
          WebResponseError({
            apiName: finishWebAuthnAuthenticationApiNameForLogging,
            httpStatusCode: e.response?.status,
          })
        )
        onError()
      },
    },
  })

  const startWebAuthnAuthenticationApiNameForLogging = 'start-webauthn-authentication'
  const getCredentialApiNameForLogging = 'navigator.credentials.get'
  const {
    mutate: startWebAuthnAuthentication,
    isLoading: isStartLoading,
    error: startError,
  } = useMutationStartWebAuthnAuthentication({
    options: { optionalRequestHeaders: getHeadersForWebAuthn(), flowToken },
    mutationOptions: {
      onSuccess: async response => {
        logCdfEvent(WebResponseSuccess({ apiName: startWebAuthnAuthenticationApiNameForLogging }))

        const requestId = response.request_id
        const decodedOptions = decodeOptions(response.options)

        if (!decodedOptions.allowCredentials?.length) {
          onError()
          return
        }

        // The passkey system dialog will show when `createCredential` is called.
        onShowPasskeyDialog?.()

        try {
          const credential = await createCredential(decodedOptions)
          if (!credential) {
            throw Error()
          }
          logCdfEvent(WebResponseSuccess({ apiName: getCredentialApiNameForLogging }))

          const encodedCredential = JSON.stringify(encodeCredential(credential))
          finishWebAuthnAuthentication({ request_id: requestId, credential: encodedCredential })
        } catch (e) {
          logCdfEvent(WebResponseError({ apiName: getCredentialApiNameForLogging }))
          throw e
        }
      },
      onError: e => {
        logCdfEvent(
          WebResponseError({
            apiName: startWebAuthnAuthenticationApiNameForLogging,
            httpStatusCode: e.response?.status,
          })
        )
        onError()
      },
    },
  })

  function startAuthentication({ phone, email }: { phone?: string; email?: string }) {
    startWebAuthnAuthentication({ sms_number: phone, email })
  }

  return {
    authenticatePasskey: ({ phone, email }: { phone?: string; email?: string }) =>
      startAuthentication({ phone, email }),
    isLoading: isStartLoading || isFinishLoading,
    error: startError || finishError ? defaultErrorMsg : undefined,
  }
}
